import { toolboxApi, unwrapResult } from '~/modules/query/base';

/** Fields every MLS-agreements record carries; the rest is entity specific. */
export interface AgreementRecord {
  [key: string]: unknown;
  id?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface AgreementMls extends AgreementRecord {}
export interface AgreementCustomer extends AgreementRecord {}
export interface AgreementMembership extends AgreementRecord {}
export interface AgreementCredential extends AgreementRecord {
  mlsId?: string;
}
export interface AgreementDocument extends AgreementRecord {
  filename?: string;
}

/** A history row: one changed field. */
export interface AgreementHistoryEntry extends AgreementRecord {
  field?: string;
}

/** Server-paged list response. */
export interface AgreementPage<T> {
  rows: T[];
  total: number;
}

/** Query params both list endpoints honour; empty values are dropped from the query string. */
export interface AgreementListArgs {
  page: number;
  pageSize: number;
  q?: string;
  status?: string;
  deleted?: boolean;
}

/** The MLS list also filters by these; the customers list ignores them, so its args cannot carry them. */
export interface AgreementMlsListArgs extends AgreementListArgs {
  vendor?: string;
  dataType?: string;
  region?: string;
}

/** Create/update body; update bodies carry `updatedAt` for the optimistic-concurrency check. */
export interface AgreementInput extends AgreementRecord {}

export interface AgreementComplianceRules extends AgreementRecord {}

export interface UploadDocumentRequest {
  file: Blob;
  entityType: string;
  entityId: string;
  docType: string;
  hasCredentials?: boolean;
}

/** GET /expiring */
export interface AgreementExpiring extends AgreementRecord {}

/** GET /credentials/{id}/secret */
export interface AgreementCredentialSecret extends AgreementRecord {}

const BASE = '/mls-agreements';

const path = (suffix = '') => `${BASE}${suffix}`;

/** The list URL's params: page and size always, the search and filters only when set. */
const listParams = (args: AgreementMlsListArgs): Record<string, string | number | boolean> => {
  const { page, pageSize, q, ...filters } = args;
  const params: Record<string, string | number | boolean> = { page, pageSize };
  if (q) {
    params.q = q;
  }
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    params[key] = value as string | number | boolean;
  }
  return params;
};

export const mlsAgreementsApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    // MLS
    listMls: builder.query<AgreementPage<AgreementMls>, AgreementMlsListArgs>({
      query: (args) => ({ url: path('/mls'), method: 'GET', params: listParams(args) }),
      transformResponse: (body: unknown) => unwrapResult<AgreementPage<AgreementMls>>(body),
      providesTags: ['AgreementMls'],
    }),

    getAgreementMls: builder.query<AgreementMls, string>({
      query: (id) => ({ url: path(`/mls/${id}`), method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementMls>(body),
      providesTags: (result, error, id) => [{ type: 'AgreementMls', id }],
    }),

    createAgreementMls: builder.mutation<AgreementMls, AgreementInput>({
      query: (data) => ({ url: path('/mls'), method: 'POST', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementMls>(body),
      invalidatesTags: ['AgreementMls', 'Expiring', 'AgreementHistory'],
    }),

    updateAgreementMls: builder.mutation<AgreementMls, { id: string; data: AgreementInput }>({
      query: ({ id, data }) => ({ url: path(`/mls/${id}`), method: 'PUT', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementMls>(body),
      invalidatesTags: ['AgreementMls', 'Expiring', 'AgreementHistory'],
    }),

    deleteAgreementMls: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/mls/${id}`), method: 'DELETE' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['AgreementMls', 'Expiring', 'AgreementHistory'],
    }),

    restoreAgreementMls: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/mls/${id}/restore`), method: 'POST' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['AgreementMls', 'Expiring', 'AgreementHistory'],
    }),

    resyncAgreementMls: builder.mutation<unknown, string>({
      query: (id) => ({ url: path(`/mls/${id}/resync`), method: 'POST' }),
      transformResponse: (body: unknown) => unwrapResult<unknown>(body),
      invalidatesTags: ['AgreementMls'],
    }),

    updateAgreementCompliance: builder.mutation<
      unknown,
      { id: string; rules: AgreementComplianceRules }
    >({
      query: ({ id, rules }) => ({
        url: path(`/mls/${id}/compliance`),
        method: 'PUT',
        data: { rules },
      }),
      transformResponse: (body: unknown) => unwrapResult<unknown>(body),
      invalidatesTags: ['AgreementMls', 'AgreementHistory'],
    }),

    // Customers
    listCustomers: builder.query<AgreementPage<AgreementCustomer>, AgreementListArgs>({
      query: (args) => ({ url: path('/customers'), method: 'GET', params: listParams(args) }),
      transformResponse: (body: unknown) => unwrapResult<AgreementPage<AgreementCustomer>>(body),
      providesTags: ['AgreementCustomer'],
    }),

    getAgreementCustomer: builder.query<AgreementCustomer, string>({
      query: (id) => ({ url: path(`/customers/${id}`), method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementCustomer>(body),
      providesTags: (result, error, id) => [{ type: 'AgreementCustomer', id }],
    }),

    createAgreementCustomer: builder.mutation<AgreementCustomer, AgreementInput>({
      query: (data) => ({ url: path('/customers'), method: 'POST', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementCustomer>(body),
      invalidatesTags: ['AgreementCustomer', 'Expiring', 'AgreementHistory'],
    }),

    updateAgreementCustomer: builder.mutation<
      AgreementCustomer,
      { id: string; data: AgreementInput }
    >({
      query: ({ id, data }) => ({ url: path(`/customers/${id}`), method: 'PUT', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementCustomer>(body),
      invalidatesTags: ['AgreementCustomer', 'Expiring', 'AgreementHistory'],
    }),

    deleteAgreementCustomer: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/customers/${id}`), method: 'DELETE' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['AgreementCustomer', 'Expiring', 'AgreementHistory'],
    }),

    restoreAgreementCustomer: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/customers/${id}/restore`), method: 'POST' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['AgreementCustomer', 'Expiring', 'AgreementHistory'],
    }),

    // Memberships
    getAgreementMembership: builder.query<AgreementMembership, string>({
      query: (id) => ({ url: path(`/memberships/${id}`), method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementMembership>(body),
      providesTags: (result, error, id) => [{ type: 'Membership', id }],
    }),

    createAgreementMembership: builder.mutation<AgreementMembership, AgreementInput>({
      query: (data) => ({ url: path('/memberships'), method: 'POST', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementMembership>(body),
      invalidatesTags: [
        'Membership',
        'AgreementMls',
        'AgreementCustomer',
        'Expiring',
        'AgreementHistory',
      ],
    }),

    updateAgreementMembership: builder.mutation<
      AgreementMembership,
      { id: string; data: AgreementInput }
    >({
      query: ({ id, data }) => ({ url: path(`/memberships/${id}`), method: 'PUT', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementMembership>(body),
      invalidatesTags: [
        'Membership',
        'AgreementMls',
        'AgreementCustomer',
        'Expiring',
        'AgreementHistory',
      ],
    }),

    deleteAgreementMembership: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/memberships/${id}`), method: 'DELETE' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: [
        'Membership',
        'AgreementMls',
        'AgreementCustomer',
        'Expiring',
        'AgreementHistory',
      ],
    }),

    restoreAgreementMembership: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/memberships/${id}/restore`), method: 'POST' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: [
        'Membership',
        'AgreementMls',
        'AgreementCustomer',
        'Expiring',
        'AgreementHistory',
      ],
    }),

    // Credentials (admin-only)
    listAgreementCredentials: builder.query<
      AgreementPage<AgreementCredential> | AgreementCredential[],
      { mlsId: string; deleted?: boolean }
    >({
      query: ({ mlsId, deleted }) => ({
        url: path(`/mls/${mlsId}/credentials`),
        method: 'GET',
        params: deleted === undefined ? undefined : { deleted },
      }),
      transformResponse: (body: unknown) =>
        unwrapResult<AgreementPage<AgreementCredential> | AgreementCredential[]>(body),
      providesTags: ['Credential'],
    }),

    createAgreementCredential: builder.mutation<
      AgreementCredential,
      AgreementInput & { mlsId: string }
    >({
      query: ({ mlsId, ...data }) => ({
        url: path(`/mls/${mlsId}/credentials`),
        method: 'POST',
        data,
      }),
      transformResponse: (body: unknown) => unwrapResult<AgreementCredential>(body),
      invalidatesTags: ['Credential', 'AgreementHistory'],
    }),

    updateAgreementCredential: builder.mutation<
      AgreementCredential,
      { id: string; data: AgreementInput }
    >({
      query: ({ id, data }) => ({ url: path(`/credentials/${id}`), method: 'PUT', data }),
      transformResponse: (body: unknown) => unwrapResult<AgreementCredential>(body),
      invalidatesTags: ['Credential', 'AgreementHistory'],
    }),

    deleteAgreementCredential: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/credentials/${id}`), method: 'DELETE' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['Credential', 'AgreementHistory'],
    }),

    restoreAgreementCredential: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/credentials/${id}/restore`), method: 'POST' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['Credential', 'AgreementHistory'],
    }),

    // A one-time reveal, so the secret stays in the panel's local state rather than in the cache.
    revealAgreementCredentialSecret: builder.mutation<AgreementCredentialSecret, string>({
      query: (id) => ({ url: path(`/credentials/${id}/secret`), method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementCredentialSecret>(body),
    }),

    // Documents
    uploadAgreementDocument: builder.mutation<AgreementDocument, UploadDocumentRequest>({
      query: ({ file, entityType, entityId, docType, hasCredentials }) => {
        const data = new FormData();
        data.append('file', file);
        data.append('entityType', entityType);
        data.append('entityId', entityId);
        data.append('docType', docType);
        data.append('hasCredentials', hasCredentials ? 'true' : 'false');
        return { url: path('/documents'), method: 'POST', data };
      },
      transformResponse: (body: unknown) => unwrapResult<AgreementDocument>(body),
      invalidatesTags: ['AgreementDocument', 'AgreementMls', 'Membership', 'AgreementHistory'],
    }),

    // The blob itself; the caller builds the anchor and clicks it.
    downloadAgreementDocument: builder.mutation<Blob, string>({
      query: (id) => ({
        url: path(`/documents/${id}/download`),
        method: 'GET',
        responseType: 'blob',
      }),
    }),

    deleteAgreementDocument: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/documents/${id}`), method: 'DELETE' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['AgreementDocument', 'AgreementMls', 'Membership', 'AgreementHistory'],
    }),

    restoreAgreementDocument: builder.mutation<AgreementRecord, string>({
      query: (id) => ({ url: path(`/documents/${id}/restore`), method: 'POST' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementRecord>(body),
      invalidatesTags: ['AgreementDocument', 'AgreementMls', 'Membership', 'AgreementHistory'],
    }),

    // History
    getAgreementHistory: builder.query<
      AgreementHistoryEntry[],
      { entityType: string; entityId: string }
    >({
      query: ({ entityType, entityId }) => ({
        url: path(`/history/${entityType}/${entityId}`),
        method: 'GET',
      }),
      transformResponse: (body: unknown) => unwrapResult<AgreementHistoryEntry[]>(body),
      providesTags: (result, error, { entityType, entityId }) => [
        { type: 'AgreementHistory', id: `${entityType}:${entityId}` },
      ],
    }),

    // Expiring
    getAgreementExpiring: builder.query<AgreementExpiring, void>({
      query: () => ({ url: path('/expiring'), method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<AgreementExpiring>(body),
      providesTags: ['Expiring'],
    }),
  }),
});

export const {
  useListMlsQuery,
  useGetAgreementMlsQuery,
  useCreateAgreementMlsMutation,
  useUpdateAgreementMlsMutation,
  useDeleteAgreementMlsMutation,
  useRestoreAgreementMlsMutation,
  useResyncAgreementMlsMutation,
  useUpdateAgreementComplianceMutation,
  useListCustomersQuery,
  useGetAgreementCustomerQuery,
  useCreateAgreementCustomerMutation,
  useUpdateAgreementCustomerMutation,
  useDeleteAgreementCustomerMutation,
  useRestoreAgreementCustomerMutation,
  useGetAgreementMembershipQuery,
  useCreateAgreementMembershipMutation,
  useUpdateAgreementMembershipMutation,
  useDeleteAgreementMembershipMutation,
  useRestoreAgreementMembershipMutation,
  useListAgreementCredentialsQuery,
  useCreateAgreementCredentialMutation,
  useUpdateAgreementCredentialMutation,
  useDeleteAgreementCredentialMutation,
  useRestoreAgreementCredentialMutation,
  useRevealAgreementCredentialSecretMutation,
  useUploadAgreementDocumentMutation,
  useDownloadAgreementDocumentMutation,
  useDeleteAgreementDocumentMutation,
  useRestoreAgreementDocumentMutation,
  useGetAgreementHistoryQuery,
  useGetAgreementExpiringQuery,
} = mlsAgreementsApi;
